/**
 * Service for handling client-side encryption using AES-GCM.
 * Encrypts sensitive election data before sending to server.
 */
import DecryptionError from './DecryptionError';

// Encryption scheme configuration
const ENCRYPTION_SCHEMES = {
    1: {
        algorithm: 'AES-GCM',
        keyLength: 128,
        ivLength: 96,
        tagLength: 96
    }
};

class EncryptionService {
    /**
     * Get encryption scheme configuration.
     * @param {number} scheme The scheme number
     * @returns {Object} The scheme configuration
     */
    getSchemeConfig(scheme) {
        const config = ENCRYPTION_SCHEMES[scheme];
        if (!config) {
            throw new Error(`Unknown encryption scheme: ${scheme}`);
        }
        return config;
    }

    /**
     * Generate a new encryption key.
     * @param {string} algorithmName Algorithm name (e.g., 'AES-GCM')
     * @param {number} keyLength Key length in bits
     * @returns {Promise<CryptoKey>} The generated key
     */
    async generateEncryptionKey(algorithmName, keyLength) {
        return await window.crypto.subtle.generateKey(
            {
                name: algorithmName,
                length: keyLength
            },
            true, // extractable
            ['encrypt', 'decrypt']
        );
    }

    /**
     * Generate a random initialization vector (IV).
     * @param {number} ivLength IV length in bits
     * @returns {Uint8Array} The generated IV
     */
    generateIV(ivLength) {
        const ivBytes = ivLength / 8;
        return window.crypto.getRandomValues(new Uint8Array(ivBytes));
    }

    /**
     * Convert ArrayBuffer to base64url encoded string (URL-safe).
     * @param {ArrayBuffer} buffer The buffer to encode
     * @returns {string} Base64url encoded string
     */
    base64urlEncode(buffer) {
        const bytes = new Uint8Array(buffer);
        const binary = Array.from(bytes, byte => String.fromCharCode(byte)).join('');
        const base64 = btoa(binary);
        // Convert to URL-safe format: replace +/= with -_
        return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
    }

    /**
     * Decode base64url string to Uint8Array.
     * @param {string} base64url Base64url encoded string
     * @returns {Uint8Array} Decoded bytes
     */
    base64urlDecode(base64url) {
        // Convert from URL-safe format: replace -_ with +/
        let base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
        // Add padding if needed
        const padding = '='.repeat((4 - base64.length % 4) % 4);
        base64 += padding;

        const binary = atob(base64);
        return Uint8Array.from(binary, char => char.charCodeAt(0));
    }

    /**
     * Export a CryptoKey to base64url format for storage.
     * @param {CryptoKey} key The key to export
     * @returns {Promise<string>} Base64url encoded key
     */
    async exportKey(key) {
        const exported = await window.crypto.subtle.exportKey('raw', key);
        return this.base64urlEncode(exported);
    }

    /**
     * Import a key from base64url format.
     * @param {string} base64urlKey Base64url encoded key
     * @param {string} algorithmName Algorithm name (e.g., 'AES-GCM')
     * @param {number} keyLength Key length in bits
     * @returns {Promise<CryptoKey>} The imported key
     */
    async importKey(base64urlKey, algorithmName, keyLength) {
        const keyData = this.base64urlDecode(base64urlKey);
        return await window.crypto.subtle.importKey(
            'raw',
            keyData,
            {
                name: algorithmName,
                length: keyLength
            },
            true,
            ['encrypt', 'decrypt']
        );
    }

    /**
     * Encrypt text using AES-GCM with unique IV per call.
     * @param {string} plaintext The text to encrypt
     * @param {Object} encryptionParams Encryption parameters
     * @param {CryptoKey} encryptionParams.key The encryption key
     * @param {number} encryptionParams.scheme The encryption scheme number
     * @returns {Promise<string>} Base64url encoded (IV || ciphertext)
     */
    async encryptText(plaintext, encryptionParams) {
        if (!plaintext || plaintext.trim() === '') {
            return plaintext;
        }

        const { key, scheme } = encryptionParams;
        const config = this.getSchemeConfig(scheme);

        // Generate unique IV for this encryption
        const iv = this.generateIV(config.ivLength);

        const encoder = new TextEncoder();
        const data = encoder.encode(plaintext);

        const encrypted = await window.crypto.subtle.encrypt(
            {
                name: config.algorithm,
                iv: iv,
                tagLength: config.tagLength
            },
            key,
            data
        );

        // Concatenate IV and ciphertext
        const ivAndCiphertext = new Uint8Array(iv.length + encrypted.byteLength);
        ivAndCiphertext.set(iv, 0);
        ivAndCiphertext.set(new Uint8Array(encrypted), iv.length);

        return this.base64urlEncode(ivAndCiphertext);
    }

    /**
     * Decrypt text using AES-GCM, extracting IV from ciphertext.
     * @param {string} ciphertext Base64url encoded (IV || ciphertext)
     * @param {Object} encryptionParams Encryption parameters
     * @param {CryptoKey} encryptionParams.key The encryption key
     * @param {number} encryptionParams.scheme The encryption scheme number
     * @returns {Promise<string>} Decrypted plaintext
     */
    async decryptText(ciphertext, encryptionParams) {
        if (!ciphertext || ciphertext.trim() === '') {
            return ciphertext;
        }

        const { key, scheme } = encryptionParams;
        const config = this.getSchemeConfig(scheme);
        const ivBytes = config.ivLength / 8;

        const ivAndCiphertext = this.base64urlDecode(ciphertext);

        // Extract IV and ciphertext
        const iv = ivAndCiphertext.slice(0, ivBytes);
        const actualCiphertext = ivAndCiphertext.slice(ivBytes);

        try {
            const decrypted = await window.crypto.subtle.decrypt(
                {
                    name: config.algorithm,
                    iv: iv,
                    tagLength: config.tagLength
                },
                key,
                actualCiphertext
            );

            const decoder = new TextDecoder();
            return decoder.decode(decrypted);
        } catch (error) {
            // Throw DecryptionError to allow caller to handle recovery logic
            throw new DecryptionError(
                `Failed to decrypt text: ${error.message}`,
                null, // electionId will be set by ApiService
                null  // attemptedWith will be set by ApiService
            );
        }
    }

    /**
     * Generate complete encryption parameters (key and scheme).
     * @param {number} scheme The encryption scheme number (default: 1)
     * @returns {Promise<Object>} Encryption parameters
     */
    async generateEncryptionParams(scheme = 1) {
        const config = this.getSchemeConfig(scheme);
        const key = await this.generateEncryptionKey(config.algorithm, config.keyLength);

        return {
            key: key,
            scheme: scheme
        };
    }

    /**
     * Export encryption parameters to storable format.
     * @param {Object} params Encryption parameters with CryptoKey
     * @returns {Promise<Object>} Storable parameters with base64url encoded key
     */
    async exportParams(params) {
        return {
            scheme: params.scheme,
            key: await this.exportKey(params.key)
        };
    }

    /**
     * Import encryption parameters from stored format.
     * @param {Object} storedParams Stored parameters with base64url encoded key
     * @returns {Promise<Object>} Encryption parameters with CryptoKey
     */
    async importParams(storedParams) {
        try {
            const config = this.getSchemeConfig(storedParams.scheme);
            return {
                scheme: storedParams.scheme,
                key: await this.importKey(storedParams.key, config.algorithm, config.keyLength)
            };
        } catch (error) {
            // Convert any error during import to DecryptionError
            throw new DecryptionError(
                `Failed to import encryption parameters: ${error.message}`,
                null, // electionId will be set by ApiService
                null  // attemptedWith will be set by ApiService
            );
        }
    }
}

// Export as singleton
const encryptionService = new EncryptionService();
export default encryptionService;
